/**
 * Calendar Store
 *
 * Manages calendar state with real-time polling updates.
 */

import { create } from 'zustand';
import { salesforceClient, type RouteListItem, type ChauffeurDto } from '../lib/salesforce';
import type { GenerateRoutesResult, RescheduleRouteResult, RitmeStand } from '../lib/salesforce/types';
import { supabase } from '../lib/supabase';
import type { MergeSnapshot } from '../lib/salesforce/merge-types';
import type { MoveSnapshot } from '../lib/salesforce/move-types';
import { useRoutePlannerStore } from './route-planner-store';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  format,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
} from 'date-fns';
import { nl } from 'date-fns/locale';

export type CalendarView = 'month' | 'week' | 'day';

export interface LastMergeState {
  snapshot: MergeSnapshot;
  sourceName: string;
  targetName: string;
  movedStopCount: number;
  expiresAt: number; // Date.now() + UNDO_WINDOW_MS
}

export interface LastMoveState {
  snapshot: MoveSnapshot;
  sourceRouteId: string;
  sourceName: string;
  targetRouteId: string;
  targetName: string;
  movedStopCount: number;
  expiresAt: number;
}

const LAST_MERGE_STORAGE_KEY = 'office.calendar.lastMerge';
const LAST_MOVE_STORAGE_KEY = 'office.calendar.lastMove';
const UNDO_WINDOW_MS = 60_000;

function persistLastMerge(value: LastMergeState | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.sessionStorage.removeItem(LAST_MERGE_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(LAST_MERGE_STORAGE_KEY, JSON.stringify(value));
    }
  } catch {
    // sessionStorage can be unavailable (private mode, quota). Ignore.
  }
}

function hydrateLastMerge(): LastMergeState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(LAST_MERGE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastMergeState;
    if (!parsed || typeof parsed.expiresAt !== 'number' || parsed.expiresAt < Date.now()) {
      window.sessionStorage.removeItem(LAST_MERGE_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persistLastMove(value: LastMoveState | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.sessionStorage.removeItem(LAST_MOVE_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(LAST_MOVE_STORAGE_KEY, JSON.stringify(value));
    }
  } catch {
    // sessionStorage can be unavailable. Ignore.
  }
}

function hydrateLastMove(): LastMoveState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(LAST_MOVE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastMoveState;
    if (!parsed || typeof parsed.expiresAt !== 'number' || parsed.expiresAt < Date.now()) {
      window.sessionStorage.removeItem(LAST_MOVE_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

interface CalendarState {
  // View state
  view: CalendarView;
  currentDate: Date;
  selectedDate: Date | null;

  // Data - routes grouped by date (YYYY-MM-DD)
  routesByDate: Record<string, RouteListItem[]>;
  chauffeurs: ChauffeurDto[];
  selectedChauffeurId: string | null;

  // Loading states
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;

  // Polling
  pollingIntervalId: ReturnType<typeof setInterval> | null;

  // Actions
  setView: (view: CalendarView) => void;
  setCurrentDate: (date: Date) => void;
  setSelectedDate: (date: Date | null) => void;
  setSelectedChauffeur: (chauffeurId: string | null) => void;

  // Navigation
  goToNext: () => void;
  goToPrevious: () => void;
  goToToday: () => void;

  // Data fetching
  fetchRoutes: () => Promise<void>;
  fetchChauffeurs: () => Promise<void>;
  refresh: () => Promise<void>;

  // Route creation
  createRoute: (data: {
    datum: string;
    routeId?: string;
    chauffeurId?: string;
    capaciteitOphalen?: number;
    capaciteitAfleveren?: number;
  }) => Promise<void>;

  // Bulk route generation
  lastGeneratePreview: GenerateRoutesResult | null;
  isGenerating: boolean;
  generateRoutes: (
    startDate: string,
    endDate: string,
    dryRun: boolean,
    ritme?: Record<string, RitmeStand>
  ) => Promise<GenerateRoutesResult>;

  // Route merging
  lastMerge: LastMergeState | null;
  isMerging: boolean;
  mergeRoutes: (sourceId: string, targetId: string, chauffeurId?: string) => Promise<void>;
  unmergeLastMerge: () => Promise<void>;
  clearLastMerge: () => void;

  // Cross-route stops move
  lastMove: LastMoveState | null;
  isMoving: boolean;
  moveStopsBetweenRoutes: (sourceId: string, targetId: string, stopIds: string[]) => Promise<void>;
  unmoveLastMove: () => Promise<void>;
  clearLastMove: () => void;

  // Route naar andere dag verplaatsen
  isRescheduling: boolean;
  isCancelling: boolean;
  /** Route annuleren (verb POST /api/routes/{id}/cancel); ruimt de rooster-rij op en ververst. */
  cancelRoute: (
    routeId: string,
    datum: string,
    body: { reden: string; toelichting?: string; stuurKlantEmail?: boolean }
  ) => Promise<{ scheduleCleanupFailed: boolean }>;
  rescheduleRoute: (
    routeId: string,
    oldDate: string,
    newDate: string,
    notifyCustomers: boolean
  ) => Promise<RescheduleRouteResult & { scheduleCleanupFailed: boolean }>;

  // Polling
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
}

/**
 * Authenticated fetch naar office-app API (Supabase-token). Lokale helper —
 * de salesforceClient dekt alleen SF-calls, dit is voor /api/workforce/*.
 */
async function authFetch<T = unknown>(url: string, options: RequestInit = {}): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error((err as { error?: string }).error || 'Request failed');
  }
  return response.json() as Promise<T>;
}

function getDateRange(date: Date, view: CalendarView): { start: string; end: string } {
  let start: Date;
  let end: Date;

  switch (view) {
    case 'month':
      start = startOfWeek(startOfMonth(date), { locale: nl });
      end = endOfWeek(endOfMonth(date), { locale: nl });
      break;
    case 'week':
      start = startOfWeek(date, { locale: nl });
      end = endOfWeek(date, { locale: nl });
      break;
    case 'day':
      start = date;
      end = date;
      break;
  }

  return {
    start: format(start, 'yyyy-MM-dd'),
    end: format(end, 'yyyy-MM-dd'),
  };
}

/**
 * De dag waar de planning over gaat. Op zondag rijdt er niets en plant
 * kantoor de week erna; dan opent de kalender op maandag in plaats van op
 * een lege week met alleen "Geen routes" (Willem 06-09).
 */
function planningsVandaag(): Date {
  const d = new Date();
  if (d.getDay() === 0) d.setDate(d.getDate() + 1);
  return d;
}

export const useCalendarStore = create<CalendarState>((set, get) => ({
  // Initial state
  view: 'week',
  currentDate: planningsVandaag(),
  selectedDate: null,
  routesByDate: {},
  chauffeurs: [],
  selectedChauffeurId: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
  pollingIntervalId: null,
  lastGeneratePreview: null,
  isGenerating: false,
  lastMerge: hydrateLastMerge(),
  isMerging: false,
  lastMove: hydrateLastMove(),
  isMoving: false,
  isRescheduling: false,
  isCancelling: false,

  // View actions
  setView: (view) => {
    set({ view });
    get().fetchRoutes();
  },

  setCurrentDate: (date) => {
    set({ currentDate: date });
    get().fetchRoutes();
  },

  setSelectedDate: (date) => set({ selectedDate: date }),

  setSelectedChauffeur: (chauffeurId) => {
    set({ selectedChauffeurId: chauffeurId });
    get().fetchRoutes();
  },

  // Navigation
  goToNext: () => {
    const { view, currentDate } = get();
    let newDate: Date;

    switch (view) {
      case 'month':
        newDate = addMonths(currentDate, 1);
        break;
      case 'week':
        newDate = addWeeks(currentDate, 1);
        break;
      case 'day':
        newDate = addDays(currentDate, 1);
        break;
    }

    set({ currentDate: newDate });
    get().fetchRoutes();
  },

  goToPrevious: () => {
    const { view, currentDate } = get();
    let newDate: Date;

    switch (view) {
      case 'month':
        newDate = subMonths(currentDate, 1);
        break;
      case 'week':
        newDate = subWeeks(currentDate, 1);
        break;
      case 'day':
        newDate = subDays(currentDate, 1);
        break;
    }

    set({ currentDate: newDate });
    get().fetchRoutes();
  },

  goToToday: () => {
    set({ currentDate: planningsVandaag() });
    get().fetchRoutes();
  },

  // Data fetching
  fetchRoutes: async () => {
    const { currentDate, view, selectedChauffeurId } = get();
    const { start, end } = getDateRange(currentDate, view);

    set({ isLoading: true, error: null });

    try {
      const routes = await salesforceClient.getCalendarRoutes(
        start,
        end,
        selectedChauffeurId || undefined
      );

      // Group routes by date (datum field is YYYY-MM-DD)
      const routesByDate: Record<string, RouteListItem[]> = {};
      for (const route of routes) {
        const dateKey = route.datum;
        if (!routesByDate[dateKey]) {
          routesByDate[dateKey] = [];
        }
        routesByDate[dateKey].push(route);
      }

      set({ routesByDate, isLoading: false, lastUpdated: new Date() });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch routes',
        isLoading: false,
      });
    }
  },

  fetchChauffeurs: async () => {
    try {
      const chauffeurs = await salesforceClient.getChauffeurs();
      set({ chauffeurs });
    } catch (error) {
      console.error('Failed to fetch chauffeurs:', error);
    }
  },

  refresh: async () => {
    await Promise.all([
      get().fetchRoutes(),
      get().fetchChauffeurs(),
    ]);
  },

  createRoute: async (data) => {
    await salesforceClient.createRoute(data);
    await get().fetchRoutes();
  },

  generateRoutes: async (startDate, endDate, dryRun, ritme) => {
    set({ isGenerating: true });
    try {
      const result = await salesforceClient.generateRoutes({ startDate, endDate, dryRun, ...(ritme ? { ritme } : {}) });
      set({ lastGeneratePreview: result, isGenerating: false });
      if (!dryRun) {
        await get().fetchRoutes();
      }
      return result;
    } catch (error) {
      set({ isGenerating: false });
      throw error;
    }
  },

  mergeRoutes: async (sourceId, targetId, chauffeurId) => {
    // Capture source + target names before the server call so the banner has context
    const { routesByDate } = get();
    let sourceName = 'route';
    let targetName = 'route';
    for (const routes of Object.values(routesByDate)) {
      for (const r of routes) {
        if (r.id === sourceId) sourceName = r.name;
        if (r.id === targetId) targetName = r.name;
      }
    }

    set({ isMerging: true });
    try {
      const result = await salesforceClient.mergeRoutes(sourceId, targetId, chauffeurId);
      const nextLastMerge: LastMergeState = {
        snapshot: result.snapshot,
        sourceName,
        targetName,
        movedStopCount: result.movedStopCount,
        expiresAt: Date.now() + UNDO_WINDOW_MS,
      };
      set({ lastMerge: nextLastMerge, isMerging: false });
      persistLastMerge(nextLastMerge);
      await get().fetchRoutes();
    } catch (error) {
      set({ isMerging: false });
      throw error;
    }
  },

  unmergeLastMerge: async () => {
    const { lastMerge } = get();
    if (!lastMerge) return;

    set({ isMerging: true });
    try {
      await salesforceClient.unmergeRoutes(lastMerge.snapshot);
      set({ lastMerge: null, isMerging: false });
      persistLastMerge(null);
      await get().fetchRoutes();
    } catch (error) {
      set({ isMerging: false });
      throw error;
    }
  },

  clearLastMerge: () => {
    set({ lastMerge: null });
    persistLastMerge(null);
  },

  cancelRoute: async (routeId, datum, body) => {
    set({ isCancelling: true });
    try {
      // 1. SF: WO's terug naar plannen, stops weg, route Geannuleerd (server-side verb)
      await salesforceClient.cancelRoute(routeId, body);

      // 2. Rooster-sync: de schedule-rij van deze route is stale (zelfde
      //    patroon als rescheduleRoute; niet blokkeren, SF is al geannuleerd).
      let scheduleCleanupFailed = false;
      try {
        const entries = await authFetch<Array<{ id: string; salesforce_route_id: string | null }>>(
          `/api/workforce/schedule?week=${datum}`
        );
        const stale = entries.find((e) => e.salesforce_route_id === routeId);
        if (stale) {
          await authFetch(`/api/workforce/schedule?id=${stale.id}`, { method: 'DELETE' });
        }
      } catch (err) {
        console.error('[Calendar] cancelRoute schedule-cleanup error:', err);
        scheduleCleanupFailed = true;
      }

      set({ isCancelling: false });
      await get().fetchRoutes();
      return { scheduleCleanupFailed };
    } catch (error) {
      set({ isCancelling: false });
      throw error;
    }
  },

  rescheduleRoute: async (routeId, oldDate, newDate, notifyCustomers) => {
    set({ isRescheduling: true });
    try {
      // 1. SF: route-datum + WO-cascade + chauffeur loskoppelen (server-side)
      const result = await salesforceClient.rescheduleRoute(routeId, newDate, notifyCustomers);

      // 2. Rooster-sync: de Supabase schedule-rij op de oude dag is stale
      //    (chauffeur is losgekoppeld; open routes hebben geen schedule-rij).
      //    Zelfde patroon als unassignRoute in schedule-store, maar via de
      //    week van de oude datum zodat dit ook vanuit de planner werkt
      //    als het rooster die week niet geladen heeft.
      let scheduleCleanupFailed = false;
      try {
        const entries = await authFetch<Array<{ id: string; salesforce_route_id: string | null }>>(
          `/api/workforce/schedule?week=${oldDate}`
        );
        const stale = entries.find((e) => e.salesforce_route_id === routeId);
        if (stale) {
          await authFetch(`/api/workforce/schedule?id=${stale.id}`, { method: 'DELETE' });
        }
      } catch (err) {
        // Niet blokkeren: SF is al verplaatst. Caller toont een waarschuwing.
        console.error('[Calendar] rescheduleRoute schedule-cleanup error:', err);
        scheduleCleanupFailed = true;
      }

      set({ isRescheduling: false });

      // 3. Refresh kalender + planner (als die deze route toont)
      await get().fetchRoutes();
      const planner = useRoutePlannerStore.getState();
      if (planner.routeId === routeId) {
        await planner.refreshRoute();
      }

      return { ...result, scheduleCleanupFailed };
    } catch (error) {
      set({ isRescheduling: false });
      throw error;
    }
  },

  moveStopsBetweenRoutes: async (sourceId, targetId, stopIds) => {
    // Capture source + target names from the calendar so the banner has context.
    const { routesByDate } = get();
    let sourceName = 'route';
    let targetName = 'route';
    for (const routes of Object.values(routesByDate)) {
      for (const r of routes) {
        if (r.id === sourceId) sourceName = r.routeName || r.name;
        if (r.id === targetId) targetName = r.routeName || r.name;
      }
    }

    set({ isMoving: true });
    try {
      const result = await salesforceClient.moveStops(sourceId, { targetRouteId: targetId, stopIds });

      // Sync the route-planner-store: clear selection and refresh the source-route view.
      const planner = useRoutePlannerStore.getState();
      planner.clearStopSelection();
      if (planner.routeId === sourceId) {
        await planner.refreshRoute();
      }

      const nextLastMove: LastMoveState = {
        snapshot: result.snapshot,
        sourceRouteId: sourceId,
        sourceName,
        targetRouteId: targetId,
        targetName,
        movedStopCount: result.movedStopCount,
        expiresAt: Date.now() + UNDO_WINDOW_MS,
      };
      set({ lastMove: nextLastMove, isMoving: false });
      persistLastMove(nextLastMove);
      await get().fetchRoutes();
    } catch (error) {
      set({ isMoving: false });
      throw error;
    }
  },

  unmoveLastMove: async () => {
    const { lastMove } = get();
    if (!lastMove) return;

    set({ isMoving: true });
    try {
      await salesforceClient.unmoveStops(lastMove.snapshot);
      set({ lastMove: null, isMoving: false });
      persistLastMove(null);

      // Refresh the planner if it is currently viewing one of the affected routes.
      const planner = useRoutePlannerStore.getState();
      if (planner.routeId === lastMove.sourceRouteId || planner.routeId === lastMove.targetRouteId) {
        await planner.refreshRoute();
      }

      await get().fetchRoutes();
    } catch (error) {
      set({ isMoving: false });
      throw error;
    }
  },

  clearLastMove: () => {
    set({ lastMove: null });
    persistLastMove(null);
  },

  // Polling for real-time updates (default: 15 minutes)
  startPolling: (intervalMs = 900000) => {
    const { pollingIntervalId } = get();

    // Clear existing interval
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
    }

    // Fetch immediately
    get().fetchRoutes();

    // Set up polling
    const id = setInterval(() => {
      get().fetchRoutes();
    }, intervalMs);

    set({ pollingIntervalId: id });
  },

  stopPolling: () => {
    const { pollingIntervalId } = get();
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      set({ pollingIntervalId: null });
    }
  },
}));
